/**
 * PeriodChangeEstimator: Home's "est. end of period" badge. A heuristic, not the forecast: the
 * cash transactions still due this calendar month plus a flat daily share of an assumed
 * monthly investment return.
 */
import {
  addDays, addMonths, daysBetween, daysInMonthOf, fromYmd, monthOf, startOfMonth, yearOf, type Day,
} from '../core/calendar.ts';
import { add, dec, div, fromDouble, fromInt, mul, sub, ZERO, type Decimal } from '../core/decimal.ts';
import { frequencyStep, scheduleDate } from '../models/frequency.ts';
import { amountOn, effectivePaymentDay } from '../models/transaction.ts';
import { debtPayment } from './debt-projector.ts';
import type { CalcContext, Transaction } from '../types/domain.ts';

/** Swift's `Decimal` literal 0.005, which goes through Decimal(Double). */
export const ASSUMED_MONTHLY_INVESTMENT_RETURN: Decimal = fromDouble(0.005);

export interface PeriodEstimate {
  readonly periodStart: Day;
  readonly periodEnd: Day;
  readonly daysRemaining: number;
  readonly cashChange: Decimal;
  readonly investmentGrowth: Decimal;
  readonly totalChange: Decimal;
}

/** The calendar month containing `now`: its first and last day. */
export function currentPeriod(now: Day): { readonly start: Day; readonly end: Day } {
  const start = startOfMonth(now);
  return { start, end: addDays(addMonths(start, 1), -1) };
}

/**
 * Candidate dates around the period. Sub-monthly and custom schedules are anchored at the
 * start (R6; a non-advancing one occurs once, at its start); monthly, quarterly and yearly ones are due once a month on the payment
 * day, this month if still ahead of today, otherwise next month.
 */
function candidateDates(transaction: Transaction, periodStart: Day, periodEnd: Day, today: Day): readonly Day[] {
  const start = transaction.startDate;
  const frequency = transaction.frequency;
  if (frequency === 'oneTime') return [start];
  if (frequency === 'monthly' || frequency === 'quarterly' || frequency === 'yearly') {
    const paymentDay = effectivePaymentDay(transaction);
    const monthLength = daysInMonthOf(today);
    const day = paymentDay === 0 ? monthLength : Math.min(paymentDay, monthLength);
    const due = fromYmd(yearOf(today), monthOf(today), day);
    return [due <= today ? addMonths(due, 1) : due];
  }

  const step = frequencyStep(frequency)!;
  if (step.count <= 0) return start >= periodStart && start <= periodEnd ? [start] : [];
  const dates: Day[] = [];
  for (let n = 0, cursor = start; cursor <= periodEnd; cursor = scheduleDate(start, step, ++n)) {
    if (cursor >= periodStart) dates.push(cursor);
  }
  return dates;
}

const signedAmount = (transaction: Transaction, now: Day): ((total: Decimal, date: Day) => Decimal) => {
  if (transaction.type === 'income') return (total, date) => add(total, amountOn(transaction, date));
  if (transaction.type === 'debt') return (total, date) => sub(total, debtPayment(transaction, date, now));
  return (total, date) => sub(total, amountOn(transaction, date));
};

/** PeriodChangeEstimator.estimate. */
export function estimatePeriodChange(
  transactions: readonly Transaction[],
  investmentBalance: Decimal,
  ctx: CalcContext,
): PeriodEstimate {
  const today = ctx.now;
  const { start: periodStart, end: periodEnd } = currentPeriod(today);
  const daysRemaining = daysBetween(today, periodEnd);

  const cashChange = transactions
    .filter((t) => t.isActive && (t.type === 'income' || t.type === 'expense' || t.type === 'debt'))
    .reduce<Decimal>((total, t) => {
      const due = candidateDates(t, periodStart, periodEnd, today).filter(
        (date) => date > today && date <= periodEnd && date >= t.startDate,
      );
      return due.reduce(signedAmount(t, today), total);
    }, ZERO);

  const dailyReturn = div(ASSUMED_MONTHLY_INVESTMENT_RETURN, dec(30));
  const investmentGrowth = mul(mul(investmentBalance, dailyReturn), fromInt(daysRemaining));
  return {
    periodStart,
    periodEnd,
    daysRemaining,
    cashChange,
    investmentGrowth,
    totalChange: add(cashChange, investmentGrowth),
  };
}
